// 連携配信（フィードフォルダ）のセールラベルの中身を組み立てる。
//
// 管理画面の feed_folder_filter.saleLabelType で形式を1つだけ選ぶ（排他）。
//   sale    … SALE
//   price   … ¥4,900（打ち消し線） ¥2,450
//   percent … 50%OFF
//
// サーバーではなくクライアントで組み立てるのは、価格と割引率が「その商品がいまセール中か」
// に依存するため。D1経由のペイロードは最大1日古くなりうるので、サーバーが文字列を焼くと
// セール終了後も古いラベルが残る（isOnSale を描画時刻で再評価しているのと同じ理由）。
//
// 戻り値を文字列ではなくパーツにしているのは price のため。元の価格に打ち消し線を引くには
// 要素を分ける必要があり、描画側（DOM組み立て / HTML文字列）で形が違うため、
// ここでは「何を出すか」だけを決めて「どう組むか」は各描画側に委ねる。
import { escapeHtml } from '@/helpers/escapeHtml';

export const FEED_FOLDER_SALE_LABEL_TYPE_SALE = 'sale';
export const FEED_FOLDER_SALE_LABEL_TYPE_PRICE = 'price';
export const FEED_FOLDER_SALE_LABEL_TYPE_PERCENT = 'percent';

// 従来の見た目。形式が未指定・不正なとき、および価格が欠けて組み立てられないときの落とし先。
// ラベルを出すと決めた商品で中身が空になると「バッジだけの空箱」が出てしまうため、
// どの分岐でも必ず表示できるものを返す
export const FEED_FOLDER_SALE_LABEL_FALLBACK_TEXT = 'SALE';

// 打ち消し線側の見た目。バッジは背景色付きなので、文字色のまま薄くして主役（セール価格）と差をつける
export const FEED_FOLDER_SALE_LABEL_ORIGINAL_STYLE = 'text-decoration:line-through;opacity:0.75;margin-right:5px;';

// ラベルの既定の配色。管理画面（feed_folder_filter）で色を選ばなかった場合の落とし先で、
// 管理画面側の FEED_FOLDER_SALE_BADGE_COLOR / FEED_FOLDER_SALE_BADGE_TEXT_COLOR と同じ値。
export const FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR = '#FFFFFF';
export const FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR = '#F5713A';

// 配色はテナントが入れた値がそのままCSSへ流れる経路なので、色として解釈できる形だけを通す。
// 不正値は既定色へ倒す（そのまま流すとブラウザが無視して色が消え、原因も追いにくい）
const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export const normalizeFeedFolderSaleLabelColor = (value, fallback) =>
  typeof value === 'string' && HEX_COLOR_PATTERN.test(value.trim())
    ? value.trim().toUpperCase()
    : fallback;

// 円は小数を持たないため整数へ丸めてから桁区切りする。
// それ以外の通貨は記号を持たないので通貨コードを前置する（単一通貨テナント前提の簡易表示）
const JPY_CURRENCY = 'JPY';

const toAmount = (value) => {
  // APIの decimal は "4900.0" のような文字列で届くため数値へ寄せる
  const amount = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = (amount, currency) => {
  const isJpy = !currency || currency === JPY_CURRENCY;

  if (isJpy) return `¥${Math.round(amount).toLocaleString('ja-JP')}`;

  const formatted = amount.toLocaleString('ja-JP', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency} ${formatted}`;
};

// 割引率は切り捨てる。四捨五入だと実際より大きい割引に見える場合があり、
// 表示上の有利誤認を避ける（27.5%OFF → 27%OFF）
const calculateDiscountRate = (price, salePrice) => Math.floor(((price - salePrice) / price) * 100);

// 価格が揃っていて、かつ実際に値下げされている場合だけ価格ベースの表示を作れる
const resolvePricePair = (product) => {
  const price = toAmount(product?.priceAmount);
  const salePrice = toAmount(product?.salePriceAmount);
  if (price === null || salePrice === null) return null;
  if (price <= 0 || salePrice >= price) return null;

  return { price, salePrice, currency: product?.priceCurrency || null };
};

const fallbackLabel = () => ({ kind: 'text', text: FEED_FOLDER_SALE_LABEL_FALLBACK_TEXT });

/**
 * セールラベルに出す中身を返す。
 *   { kind: 'text', text }              … SALE / 50%OFF
 *   { kind: 'price', original, sale }   … 打ち消し線付きの元価格とセール価格
 * 組み立てられない場合は必ず kind: 'text' の SALE を返すので、呼び出し側は空を気にしなくてよい。
 */
export const buildFeedFolderSaleLabel = (product, saleLabelType) => {
  if (saleLabelType !== FEED_FOLDER_SALE_LABEL_TYPE_PRICE
    && saleLabelType !== FEED_FOLDER_SALE_LABEL_TYPE_PERCENT) {
    return fallbackLabel();
  }

  const pair = resolvePricePair(product);
  if (!pair) return fallbackLabel();

  if (saleLabelType === FEED_FOLDER_SALE_LABEL_TYPE_PRICE) {
    return {
      kind: 'price',
      original: formatAmount(pair.price, pair.currency),
      sale: formatAmount(pair.salePrice, pair.currency),
    };
  }

  const rate = calculateDiscountRate(pair.price, pair.salePrice);
  // 1%未満の値下げは「0%OFF」になってしまうのでラベルとして成立しない
  return rate > 0 ? { kind: 'text', text: `${rate}%OFF` } : fallbackLabel();
};

/**
 * ラベルの中身を要素へ流し込む（DOMを直接組む描画経路用）。
 * 同じ要素が別の商品へ使い回されても前の中身が残らないよう、必ず空にしてから組み直す。
 */
export const applyFeedFolderSaleLabel = (element, label) => {
  if (!element) return;

  element.textContent = '';

  if (label?.kind !== 'price') {
    element.textContent = label?.text || FEED_FOLDER_SALE_LABEL_FALLBACK_TEXT;
    return;
  }

  const original = document.createElement('span');
  original.style.cssText = FEED_FOLDER_SALE_LABEL_ORIGINAL_STYLE;
  original.textContent = label.original;

  const sale = document.createElement('span');
  sale.textContent = label.sale;

  element.appendChild(original);
  element.appendChild(sale);
};

/**
 * ラベルの中身をHTML文字列で返す（innerHTMLで組み立てる描画経路用）。
 * 金額はこのモジュールが生成した文字列だが、経路を問わずエスケープを通しておく。
 */
export const buildFeedFolderSaleLabelHtml = (label) => {
  if (label?.kind !== 'price') {
    return escapeHtml(label?.text || FEED_FOLDER_SALE_LABEL_FALLBACK_TEXT);
  }

  return `<span style="${FEED_FOLDER_SALE_LABEL_ORIGINAL_STYLE}">${escapeHtml(label.original)}</span>`
    + `<span>${escapeHtml(label.sale)}</span>`;
};
